import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../config/api';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { ToastContext } from '../App';

interface Animal {
  id: number;
  name: string;
  birth_date: string;
  observations?: string;
  user_id?: number;
  is_active?: boolean | number;
}

interface Student {
  id: number;
  name: string;
  email: string;
  student_category: string;
}

interface Slot {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  is_blocked: boolean;
}

interface Booking {
  id: number;
  availability_slot_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  student_category?: string;
  animal_id: number;
  animal_name: string;
  observations?: string;
  created_at: string;
}

interface WeatherHour {
  datetime: string;
  temp: number;
  icon: string;
  description: string;
}

interface WeatherData {
  hourly?: WeatherHour[];
}

interface WeatherInfo {
  temp: number;
  icon: string;
  description: string;
}

interface Settings {
  min_booking_hours_ahead?: string;
  bookings_blocked?: string;
}

const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [calendarView, setCalendarView] = useState<'daily' | 'weekly'>('daily');
  const [error, setError] = useState('');
  const { showToast } = React.useContext(ToastContext);
  const [settings, setSettings] = useState<Settings>({});

  // Estado principal do calendário
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [weekDates, setWeekDates] = useState<string[]>([]);
  const [weeklySlots, setWeeklySlots] = useState<Record<string, Slot[]>>({});
  const [weeklyBookings, setWeeklyBookings] = useState<Booking[]>([]);

  // Estados de loading específicos
  const [dailyLoading, setDailyLoading] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState<Record<string, boolean>>({});

  // Modal de marcação
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedAnimalId, setSelectedAnimalId] = useState<number | string | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Estado para email semanal
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Estados para modal de confirmação
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<Booking | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (calendarView === 'daily') {
      loadDailyCalendar(selectedDate);
    } else {
      loadWeeklyCalendar(selectedDate);
    }
  }, [selectedDate, calendarView]);

  const loadSettings = async () => {
    try {
      const data = await apiCall('/settings', { method: 'GET' });
      if (data.settings) {
        const settingsMap: Settings = {};
        if (Array.isArray(data.settings)) {
          data.settings.forEach((setting: any) => {
            settingsMap[setting.setting_key as keyof Settings] = setting.setting_value;
          });
        } else if (typeof data.settings === 'object') {
          Object.keys(data.settings).forEach(key => {
            // Para o formato novo onde as configurações têm value e description
            if (data.settings[key] && typeof data.settings[key] === 'object' && 'value' in data.settings[key]) {
              settingsMap[key as keyof Settings] = data.settings[key].value;
            } else {
              settingsMap[key as keyof Settings] = data.settings[key];
            }
          });
        }
        setSettings(settingsMap);
      }
    } catch (error) {
      // Não bloquear o calendário se falhar
    }
  };

  // Carregar slots, bookings e weather para o dia
  const loadDailyCalendar = async (date: string) => {
    setDailyLoading(true);
    setError('');
    try {
      const [slotsResponse, bookingsResponse] = await Promise.all([
        apiCall(`/availability/date/${date}`),
        user?.role === 'trainer' ? apiCall('/bookings/all') : apiCall('/bookings/my')
      ]);
      setSlots(slotsResponse.slots || []);
      setBookings(bookingsResponse.bookings || []);
      
      // Carregar dados meteorológicos
      try {
        const weatherResponse = await apiCall('/weather/forecast?days=5');
        if (weatherResponse && !weatherResponse.error) {
          setWeatherData({ hourly: weatherResponse.hourly_data || [] });
        }
      } catch (error) {
        console.log('Weather error (daily):', error);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar calendário');
    } finally {
      setDailyLoading(false);
    }
  };

  // Carregar slots, bookings e weather para a semana
  const loadWeeklyCalendar = async (startDate: string) => {
    setError('');
    try {
      const start = new Date(startDate);
      const dayOfWeek = start.getDay();
      const monday = new Date(start);
      monday.setDate(start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
      const weekDates: string[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        weekDates.push(date.toISOString().split('T')[0]);
      }
      setWeekDates(weekDates);
      
      // Inicializar loading para cada dia
      const initialLoading: Record<string, boolean> = {};
      weekDates.forEach(date => {
        initialLoading[date] = true;
      });
      setWeeklyLoading(initialLoading);
      
      const slotsPromises = weekDates.map(date => apiCall(`/availability/date/${date}`));
      const bookingsResponse = user?.role === 'trainer' ? await apiCall('/bookings/all') : await apiCall('/bookings/my');
      const slotsResponses = await Promise.all(slotsPromises);
      const weeklySlotsData: Record<string, Slot[]> = {};
      weekDates.forEach((date, index) => {
        weeklySlotsData[date] = slotsResponses[index].slots || [];
      });
      setWeeklySlots(weeklySlotsData);
      setWeeklyBookings(bookingsResponse.bookings || []);
      
      // Limpar loading para todos os dias
      setWeeklyLoading({});
      
      // Carregar dados meteorológicos
      try {
        const weatherResponse = await apiCall('/weather/forecast?days=5');
        if (weatherResponse && !weatherResponse.error) {
          setWeatherData({ hourly: weatherResponse.hourly_data || [] });
        }
      } catch (error) {
        console.log('Weather error (weekly):', error);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar calendário semanal');
      setWeeklyLoading({});
    }
  };

  // Função para saber se um slot está disponível (treinador pode marcar slots passados)
  const isSlotAvailable = (slot: Slot, bookingsArr: Booking[]) => {
    const now = new Date();
    const slotStart = new Date(`${slot.date}T${slot.start_time}`);
    const isCurrent = Math.abs(slotStart.getTime() - now.getTime()) < 60 * 60 * 1000; // 1 hora
    
    if (user?.role === 'trainer') {
      return !slot.is_blocked && !bookingsArr.find(b => b.availability_slot_id === slot.id);
    }
    
    // Para alunos, verificar se o slot é futuro e não está bloqueado
    return slotStart > now && !slot.is_blocked && !bookingsArr.find(b => b.availability_slot_id === slot.id);
  };

  const getWeatherIconClass = (iconCode: string) => {
    const iconMap: { [key: string]: string } = {
      '01d': 'fas fa-sun text-warning',
      '01n': 'fas fa-moon text-secondary',
      '02d': 'fas fa-cloud-sun text-warning',
      '02n': 'fas fa-cloud-moon text-secondary',
      '03d': 'fas fa-cloud text-secondary',
      '03n': 'fas fa-cloud text-secondary',
      '04d': 'fas fa-cloud text-secondary',
      '04n': 'fas fa-cloud text-secondary',
      '09d': 'fas fa-cloud-rain text-info',
      '09n': 'fas fa-cloud-rain text-info',
      '10d': 'fas fa-cloud-sun-rain text-info',
      '10n': 'fas fa-cloud-moon-rain text-info',
      '11d': 'fas fa-bolt text-warning',
      '11n': 'fas fa-bolt text-warning',
      '13d': 'fas fa-snowflake text-info',
      '13n': 'fas fa-snowflake text-info',
      '50d': 'fas fa-smog text-secondary',
      '50n': 'fas fa-smog text-secondary'
    };
    return iconMap[iconCode] || 'fas fa-question text-muted';
  };

  const getWeatherForSlot = (slotTime: string, weatherData: WeatherData | null, slotDate: string): WeatherInfo | null => {
    if (!weatherData || !weatherData.hourly || weatherData.hourly.length === 0) {
      return null;
    }

    // Converter hora do slot para formato comparável (HH:MM)
    const slotHour = parseInt(slotTime.split(':')[0]);
    const slotMinute = parseInt(slotTime.split(':')[1]);

    // Encontrar a previsão mais próxima para a data específica
    let closestHour: WeatherHour | null = null;
    let minDifference = Infinity;

    for (const hourData of weatherData.hourly) {
      const hourDateTime = new Date(hourData.datetime);
      const hourDate = hourDateTime.toISOString().split('T')[0]; // YYYY-MM-DD
      
      // Só considerar dados da mesma data
      if (hourDate === slotDate) {
        const hourHour = hourDateTime.getHours();
        const hourMinute = hourDateTime.getMinutes();

        // Calcular diferença em minutos
        const difference = Math.abs((hourHour * 60 + hourMinute) - (slotHour * 60 + slotMinute));
        
        if (difference < minDifference) {
          minDifference = difference;
          closestHour = hourData;
        }
      }
    }

    if (closestHour) {
      return {
        temp: Math.round(closestHour.temp),
        icon: closestHour.icon,
        description: closestHour.description
      };
    }

    return null;
  };

  const openBookingModal = async (slotId: number, date: string, time: string) => {
    if (user?.role === 'guest') {
      showToast('Convidados não podem marcar aulas', 'error');
      return;
    }

    // Verificar se as marcações estão bloqueadas (apenas para alunos)
    if (user?.role === 'student' && settings.bookings_blocked === '1') {
      showToast('As marcações estão temporariamente bloqueadas. Contacte o treinador para mais informações.', 'error');
      return;
    }

    try {
      setBookingLoading(true);
      const [animalsResponse, studentsResponse] = await Promise.all([
        user?.role === 'trainer' ? apiCall('/animals/all') : apiCall('/animals/my?active=1'),
        user?.role === 'trainer' ? apiCall('/auth/students') : Promise.resolve({ students: [] })
      ]);
      setAnimals(animalsResponse.animals || []);
      setStudents(studentsResponse.students || []);
      setSelectedSlot({ id: slotId, date, start_time: time, end_time: '', is_blocked: false });
      setShowBookingModal(true);
    } catch (error) {
      showToast('Erro ao carregar dados para marcação', 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedSlot(null);
    setSelectedAnimalId(null);
  };

  const handleCreateBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot || !selectedAnimalId) return;

    try {
      setBookingLoading(true);
      const [userId, animalId] = selectedAnimalId.toString().includes(':') 
        ? selectedAnimalId.toString().split(':').map(Number)
        : [user?.id, selectedAnimalId];

      await apiCall('/bookings', {
        method: 'POST',
        body: JSON.stringify({
          availability_slot_id: selectedSlot.id,
          animal_id: animalId,
          user_id: userId
        })
      });

      showToast('Aula marcada com sucesso!', 'success');
      closeBookingModal();
      
      // Recarregar calendário
      if (calendarView === 'daily') {
        loadDailyCalendar(selectedDate);
      } else {
        loadWeeklyCalendar(selectedDate);
      }
    } catch (error: any) {
      showToast(error.message || 'Erro ao marcar aula', 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  const showCancelConfirmation = (booking: Booking) => {
    setBookingToCancel(booking);
    setShowCancelModal(true);
  };

  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;

    try {
      await apiCall(`/bookings/${bookingToCancel.id}`, { method: 'DELETE' });
      showToast('Aula cancelada com sucesso!', 'success');
      
      // Recarregar calendário
      if (calendarView === 'daily') {
        loadDailyCalendar(selectedDate);
      } else {
        loadWeeklyCalendar(selectedDate);
      }
      setShowCancelModal(false);
      setBookingToCancel(null);
    } catch (error: any) {
      showToast(error.message || 'Erro ao cancelar aula', 'error');
    }
  };

  const sendWeeklyEmail = async () => {
    try {
      setSendingEmail(true);
      
      // Calcular datas da semana
      const start = new Date(selectedDate);
      const dayOfWeek = start.getDay();
      const monday = new Date(start);
      monday.setDate(start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
      
      const endDate = new Date(monday);
      endDate.setDate(monday.getDate() + 6);

      const startDateStr = monday.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      await apiCall('/availability/send-weekly-email', {
        method: 'PUT',
        body: JSON.stringify({
          start_date: startDateStr,
          end_date: endDateStr
        })
      });

      showToast('Email de disponibilidade semanal enviado com sucesso!', 'success');
    } catch (error: any) {
      showToast(error.message || 'Erro ao enviar email semanal', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const renderSlot = (slot: Slot, bookingsArr: Booking[], isWeekly: boolean = false, isNextSlot: boolean = false) => {
    const booking = bookingsArr.find(b => b.availability_slot_id === slot.id);
    const available = isSlotAvailable(slot, bookingsArr);
    const now = new Date();
    const slotStart = new Date(`${slot.date}T${slot.start_time}`);
    const slotEnd = new Date(`${slot.date}T${slot.end_time}`);
    const isCurrent = now >= slotStart && now <= slotEnd;

    // Formatar horas para HH:MM
    const formatHour = (h: string) => h.slice(0, 5);

    // Definir classe de cor do slot (usar sempre as weekly-slot para ambos os modos)
    let slotClass = '';
    if (slot.is_blocked) {
      slotClass = 'calendar-slot-blocked';
    } else if (booking) {
      if (user?.role === 'student' && booking.user_id === user?.id) {
        slotClass = 'weekly-slot my-booking';
      } else {
        slotClass = 'weekly-slot booked';
      }
    } else if (available) {
      slotClass = 'calendar-slot-available';
    } else {
      slotClass = 'calendar-slot-blocked';
    }

    const handleSlotClick = () => {
      if (available && user?.role !== 'guest') {
        openBookingModal(slot.id, slot.date, slot.start_time);
      }
    };

    // Obter dados meteorológicos para o slot (apenas para slots futuros ou atuais)
    const weatherInfo = (slotStart > now || isCurrent) ? getWeatherForSlot(slot.start_time, weatherData, slot.date) : null;

    return (
      <div key={slot.id} className={isWeekly ? "col-12 mb-2" : "col-md-6 col-lg-3 mb-3"}>
        <div
          className={`card ${slotClass}`}
          style={{ cursor: (available && user?.role !== 'guest') ? 'pointer' : 'default' }}
          onClick={handleSlotClick}
        >
          <div className="card-header d-flex justify-content-between align-items-center" style={{ border: 'none', background: 'transparent' }}>
            <div className="d-flex align-items-center">
              <span className="me-2 calendar-slot-hour">
                {formatHour(slot.start_time)} - {formatHour(slot.end_time)}
              </span>
              {weatherInfo && (
                <span className="text-muted small ms-2">
                  <i className={`${getWeatherIconClass(weatherInfo.icon)} me-1`}></i>
                  {weatherInfo.temp}°C
                </span>
              )}
            </div>
            <div>
              {isCurrent && booking && <span className="badge-agora-ex me-1">Agora</span>}
              {isNextSlot && !isCurrent && <span className="badge-seguinte me-1">Seguinte</span>}
              {booking && !slot.is_blocked && ((user?.role === 'trainer') || (user?.role === 'student' && booking.user_id === user?.id)) && (
                <button
                  className="btn btn-sm btn-remove-booking"
                  onClick={(e) => { e.stopPropagation(); showCancelConfirmation(booking); }}
                  title="Cancelar"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
          <div className="card-body calendar-slot-content">
            {slot.is_blocked ? (
              <>
                <i className="fas fa-ban fa-2x mb-2"></i>
                <div>Bloqueado</div>
              </>
            ) : booking ? (
              <>
                {user?.role === 'trainer' ? (
                  <div><i className="fas fa-user me-1"></i><span style={{fontSize: '1rem'}}>{booking.user_name}</span> com <span style={{fontSize: '1rem'}}>{booking.animal_name}</span></div>
                ) : booking.user_id === user?.id ? (
                  <div><i className="fas fa-check me-1"></i><span style={{fontSize: '1rem'}}>{booking.animal_name}</span></div>
                ) : (
                  (booking.student_category === 'external' && booking.user_id !== user?.id) || 
                  (user?.student_category === 'external' && booking.user_id !== user?.id) ? (
                    <div><i className="fas fa-lock me-1"></i>Aula Privada</div>
                  ) : (
                    <div><i className="fas fa-user me-1"></i><span style={{fontSize: '1rem'}}>{booking.user_name}</span> com <span style={{fontSize: '1rem'}}>{booking.animal_name}</span></div>
                  )
                )}
                {booking.observations && <div className="small mt-1"><strong>Obs:</strong> {booking.observations}</div>}
              </>
            ) : available ? (
              <div>
                <i className="fas fa-plus me-1"></i>Disponível
              </div>
            ) : (
              <div className="text-muted">Indisponível</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Renderização da vista diária
  const renderDailyView = () => {
    // Identificar o próximo slot com marcação (booking)
    const now = new Date();
    let nextSlot: Slot | null = null;
    
    // Verificar se há um slot atual
    const currentSlot = slots.find(slot => {
      const slotStart = new Date(slot.date + 'T' + slot.start_time);
      const slotEnd = new Date(slot.date + 'T' + slot.end_time);
      return now >= slotStart && now <= slotEnd;
    });
    
    // Procurar o próximo slot futuro com booking (não bloqueado, não o atual)
    const nextBookedFutureSlots = slots.filter(slot => {
      const slotDateTime = new Date(slot.date + 'T' + slot.start_time);
      const isNotCurrent = !currentSlot || slot.id !== currentSlot.id;
      const hasBooking = bookings.some(b => b.availability_slot_id === slot.id);
      return slotDateTime > now && !slot.is_blocked && isNotCurrent && hasBooking;
    }).sort((a, b) => {
      const aTime = new Date(a.date + 'T' + a.start_time);
      const bTime = new Date(b.date + 'T' + b.start_time);
      return aTime.getTime() - bTime.getTime();
    });
    
    if (nextBookedFutureSlots.length > 0) {
      nextSlot = nextBookedFutureSlots[0];
    }

    if (dailyLoading) {
      return (
        <div className="row">
          <div className="col-12">
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    return (
      <div className="row">
        {slots.length === 0 ? (
          <div className="col-12"><p className="text-center text-muted">Nenhuma disponibilidade para esta data.</p></div>
        ) : (
          slots.map(slot => renderSlot(slot, bookings, false, nextSlot ? slot.id === nextSlot.id : false))
        )}
      </div>
    );
  };

  // Renderização da vista semanal
  const renderWeeklyView = () => {
    // Identificar o próximo slot com marcação em toda a semana
    const now = new Date();
    let nextSlot: Slot | null = null;
    
    // Verificar se há um slot atual
    let currentSlot: Slot | null = null;
    weekDates.forEach(date => {
      const slots = weeklySlots[date] || [];
      slots.forEach(slot => {
        const slotStart = new Date(slot.date + 'T' + slot.start_time);
        const slotEnd = new Date(slot.date + 'T' + slot.end_time);
        if (now >= slotStart && now <= slotEnd) {
          currentSlot = slot;
        }
      });
    });

    // Procurar o próximo slot futuro com booking (não bloqueado, não o atual)
    const nextBookedFutureSlots: Slot[] = [];
    weekDates.forEach(date => {
      const slots = weeklySlots[date] || [];
      slots.forEach(slot => {
        const slotDateTime = new Date(slot.date + 'T' + slot.start_time);
        const isFuture = slotDateTime > now;
        const isNotCurrent = !currentSlot || slot.id !== currentSlot.id;
        const hasBooking = weeklyBookings.some(b => b.availability_slot_id === slot.id);
        if (isFuture && !slot.is_blocked && isNotCurrent && hasBooking) {
          nextBookedFutureSlots.push(slot);
        }
      });
    });
    
    nextBookedFutureSlots.sort((a, b) => {
      const aTime = new Date(a.date + 'T' + a.start_time);
      const bTime = new Date(b.date + 'T' + b.start_time);
      return aTime.getTime() - bTime.getTime();
    });
    
    if (nextBookedFutureSlots.length > 0) {
      nextSlot = nextBookedFutureSlots[0];
    }

    return (
      <div>
        {/* Removido o botão duplicado 'Enviar Email Semanal' */}
        <div className="row">
          {weekDates.length === 0 ? (
            <div className="col-12"><p className="text-center text-muted">A carregar semana...</p></div>
          ) : (
            weekDates
              .filter(date => {
                const slots = weeklySlots[date] || [];
                const isLoading = weeklyLoading[date];
                // Mostrar apenas dias que têm slots ou que ainda estão carregando
                return isLoading || slots.length > 0;
              })
              .map(date => {
                const slots = weeklySlots[date] || [];
                const isLoading = weeklyLoading[date];
                
                return (
                  <div key={date} className="col-md-6 col-lg-3 mb-3">
                    <div className="card">
                      <div className="card-header weekly-day-header">
                        <h6 className="mb-0">{new Date(date).toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: '2-digit' })}</h6>
                      </div>
                      <div className="card-body">
                        {isLoading ? (
                          <div className="d-flex justify-content-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Carregando...</span>
                            </div>
                          </div>
                        ) : (
                          <div className="row">
                            {slots.map(slot => renderSlot(slot, weeklyBookings, true, nextSlot ? slot.id === nextSlot.id : false))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>
    );
  };

  // Modal de marcação
  const renderBookingModal = () => (
    showBookingModal && selectedSlot && (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Marcar Aula</h5>
              <button type="button" className="btn-close" onClick={closeBookingModal}></button>
            </div>
            <form onSubmit={handleCreateBooking}>
              <div className="modal-body">
                <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '.95em' }}>
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Regra de Marcação:</strong> É necessário marcar com pelo menos 24 horas de antecedência (exceto segunda-feira).
                </div>
                <div className="mb-3">
                  <label htmlFor="animalSelect" className="form-label">
                    {user?.role === 'trainer' ? 'Selecionar Aluno e Animal:' : 'Selecionar Animal:'}
                  </label>
                  <select className="form-select" id="animalSelect" value={selectedAnimalId || ''} onChange={e => setSelectedAnimalId(e.target.value)} required>
                    <option value="">{user?.role === 'trainer' ? 'Selecionar aluno e animal...' : 'Selecionar animal...'}</option>
                    {user?.role === 'trainer'
                      ? students
                          .filter(student => animals.some(animal => animal.user_id === student.id))
                          .map(student => {
                            const studentAnimals = animals.filter(a => a.user_id === student.id);
                            return (
                              <optgroup key={student.id} label={student.name}>
                                {studentAnimals.map(animal => (
                                  <option key={animal.id} value={`${student.id}:${animal.id}`}>{student.name} - {animal.name}</option>
                                ))}
                              </optgroup>
                            );
                          })
                      : animals.map(animal => (
                          <option key={animal.id} value={animal.id}>{animal.name}</option>
                        ))}
                  </select>
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeBookingModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#a2449c', border: 'none' }} disabled={bookingLoading}>{bookingLoading ? 'A processar...' : 'Marcar Aula'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  );



  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h5><i className="fas fa-calendar me-2"></i>Calendário de Aulas</h5>
            </div>
            <div className="card-body">
              <div className="row mb-3 align-items-end">
                <div className="col-md-4">
                  <label htmlFor="calendarDate" className="form-label">Selecionar Data:</label>
                  <div className="input-group">
                    <button 
                      className="btn btn-outline-light" 
                      type="button"
                      style={{ borderColor: '#dee2e6', color: '#6c757d' }}
                      onClick={() => {
                        const currentDate = new Date(selectedDate);
                        currentDate.setDate(currentDate.getDate() - 1);
                        setSelectedDate(currentDate.toISOString().slice(0, 10));
                      }}
                      title="Dia anterior"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <input 
                      type="date" 
                      className="form-control" 
                      id="calendarDate" 
                      value={selectedDate} 
                      onChange={e => setSelectedDate(e.target.value)} 
                    />
                    <button 
                      className="btn btn-outline-light" 
                      type="button"
                      style={{ borderColor: '#dee2e6', color: '#6c757d' }}
                      onClick={() => {
                        const currentDate = new Date(selectedDate);
                        currentDate.setDate(currentDate.getDate() + 1);
                        setSelectedDate(currentDate.toISOString().slice(0, 10));
                      }}
                      title="Dia seguinte"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
                <div className="col-md-4">
                  <label htmlFor="calendarView" className="form-label">Vista:</label>
                  <select className="form-select" id="calendarView" value={calendarView} onChange={e => setCalendarView(e.target.value as 'daily' | 'weekly')}>
                    <option value="daily">Vista Diária</option>
                    <option value="weekly">Vista Semanal</option>
                  </select>
                </div>
                {calendarView === 'weekly' && user?.role === 'trainer' && (
                  <div className="col-md-4 d-flex align-items-end justify-content-md-end mt-3 mt-md-0">
                    <button
                      className="btn btn-success w-100"
                      onClick={sendWeeklyEmail}
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          A enviar...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-envelope me-2"></i>
                          Enviar Email Semanal
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              {error && <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>}
              {calendarView === 'daily' ? renderDailyView() : renderWeeklyView()}
            </div>
          </div>
        </div>
      </div>
      {renderBookingModal()}
      
      {/* Modal de Confirmação para Cancelar Aula */}
      <ConfirmationModal
        isOpen={showCancelModal}
        title="Cancelar Aula"
        message="Tem certeza que deseja cancelar esta aula?"
        confirmText="Cancelar"
        cancelText="Manter"
        confirmButtonClass="btn-danger"
        onConfirm={handleCancelBooking}
        onCancel={() => {
          setShowCancelModal(false);
          setBookingToCancel(null);
        }}
      />
      
    </div>
  );
};

export default CalendarPage; 